import React, { useState, useEffect } from 'react';
import { Tag, Tooltip, Space, Badge, Button, Modal, Spin } from 'antd';
import {
  ClockCircleOutlined,
  CarOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  WarningOutlined
} from '@ant-design/icons';
import moment from 'moment';
import ordersService from '../../../services/ordersService';
import userRoleUtils from '../../../utils/userRoleUtils';
import { hasSchedulingIssue } from '../../../utils/orderUtils';
import { getOnfleetImageUrls, getOrderCompletionDetails, getOrderCompletionTime } from '../../../utils/onfleetUtils';
import DeliveryImageCarousel from './DeliveryImageCarousel';

const OrderStatusCell = ({ record, showSchedulingWarnings = false }) => {
  const [etaInfo, setEtaInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [taskDetails, setTaskDetails] = useState(null);
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const isAdmin = userRoleUtils.isAdmin();

  const isEnRoute =
    record.order_status === 'En-route' ||
    record.order_status === 'On-Route' ||
    record.order_status === 'en-route' ||
    record.order_status === 'on-route';

  const isCompletedOrCollected =
    record.order_status === 'Completed' ||
    record.order_status === 'Collected';

  useEffect(() => {
    if (isEnRoute) {
      fetchEtaInfo();
      const intervalId = setInterval(() => {
        fetchEtaInfo();
      }, 10000);
      return () => clearInterval(intervalId);
    }
  }, [record.id, record.order_status]);

  const fetchEtaInfo = async () => {
    try {
      setLoading(true);
      const response = await ordersService.getOrderEtaInfo(record.id);
      if (response && response.order) {
        setEtaInfo(response.order);
      }
    } catch (err) {
      console.error('Error fetching ETA info:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTaskDetails = async (record) => {
    try {
      setSyncing(true);
      if (record.task_id && !record.onfleet_task_json) {
        const data = await ordersService.syncOnfleetTask(record.id);
        setTaskDetails(data?.task || data);
      } else if (record.onfleet_task_json) {
        const info =  JSON.parse(record.onfleet_task_json)
        setTaskDetails(info);
      }
    } catch (e) {
      console.warn('Failed to load task info', e);
    } finally {
      setSyncing(false);
    }
  };

  const handleTaskInfoClick = async () => {
    await loadTaskDetails(record);
    setIsTaskModalVisible(true);
  };

  const getOrderStatusTag = (record) => {
    switch (record.order_status) {
      case 'Scheduled':
        return <span>
          <Tag color="blue" className="m-0">Scheduled</Tag>
          {record.onfleet_task_position > 0 &&<i style={{ marginLeft: '5px',fontSize: '8px' }}>
           <br/>Orders in Queue: {record.onfleet_task_position}
        </i>}
      </span>;
      case 'En-route':
      case 'On-Route':
        return <Tag color="orange">En-Route</Tag>;
      case 'Completed':
        return <Tag color="green">Completed</Tag>;
      case 'Collected':
        return <Tag color="purple">Collected</Tag>;
      case 'Cancelled':
        return <Tag color="red">Cancelled</Tag>;
      case 'Failed':
        return <Tag color="volcano">Failed</Tag>;
      default:
        return <Tag color="default">{record.order_status}</Tag>;
    }
  };

  const renderImages = (details) => {
    const images = getOnfleetImageUrls(details);
    return images.length ? (
      <div className="mt-4">
        <p className="font-medium mb-2">Delivery Images:</p>
        <DeliveryImageCarousel images={images} maxHeight={300} />
      </div>
    ) : null;
  };

  const renderAdminFields = (task) => {
    if (!isAdmin) return null;
    const taskInfo = task?.task || task?.data?.task || task;
    return (
      <div className="mt-4 text-sm text-gray-700 border-t pt-4">
         {taskInfo?.trackingURL && <p><strong>Tracking URL:</strong> <a href={taskInfo.trackingURL} target="_blank" rel="noreferrer" className="text-blue-500">{taskInfo.trackingURL}</a></p>}
        {taskInfo?.feedback && (
          <p><strong>Feedback:</strong> {taskInfo.feedback.rating}/5 - {taskInfo.feedback.comments}</p>
        )}
        {taskInfo?.notes && (
          <p><strong>Notes:</strong> {taskInfo.notes}</p>
        )}
      </div>
    );
  };

  if (hasSchedulingIssue(record) && showSchedulingWarnings) {
    return (
      <Space size={4} align="center">
        {getOrderStatusTag(record)}
        <Tooltip title="This order is not properly scheduled. Missing task ID.">
          <WarningOutlined aria-label="Scheduling warning" style={{ color: '#fa8c16' }} />
        </Tooltip>
      </Space>
    );
  }

  if (isCompletedOrCollected) {
    const completionDetails = getOrderCompletionDetails(record, taskDetails);
    const completionTime = getOrderCompletionTime(record, taskDetails);

    return (
      <div>
        {getOrderStatusTag(record)}
        <div className="mt-1">
          <Button
            type="link"
            size="small"
            onClick={handleTaskInfoClick}
            className="p-0 text-xs text-blue-500 hover:text-blue-700"
          >
            <InfoCircleOutlined className="mr-1" />
            Task Info
          </Button>
        </div>

        <Modal
          title={`Delivery Info - Order #${record.id}`}
          open={isTaskModalVisible}
          onCancel={() => setIsTaskModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsTaskModalVisible(false)}>
              Close
            </Button>
          ]}
        >
          {syncing ? (
            <div className="text-center text-gray-500">
              <Spin size="large" className="my-6" /> Loading task info…
            </div>
          ) : completionDetails || completionTime ? (
            <div className="text-sm text-gray-700">
              {completionDetails && (
                <p><strong>Status:</strong> {completionDetails.success === false ? 'Failed' : 'Success'}</p>
              )}
              {completionTime && (
                <p><strong>Delivered:</strong> {moment(completionTime).format('DD MMM YYYY, HH:mm')}</p>
              )}
              {completionDetails?.notes && <p><strong>Notes:</strong> {completionDetails.notes}</p>}
              {renderImages(completionDetails)}
              {renderAdminFields(taskDetails)}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              <p>No task data available.</p>
            </div>
          )}
        </Modal>
      </div>
    );
  }

  if (isEnRoute) {
    return (
      <div>
        {getOrderStatusTag(record)}
        <div className="mt-1">
          {loading && !etaInfo ? (
            <div className="flex items-center text-xs text-gray-500">
              <LoadingOutlined className="mr-1" />
              <span>Loading ETA...</span>
            </div>
          ) : etaInfo ? (
            <Tooltip
              title={
                <div>
                  <p>Driver: {etaInfo.driver_name || 'Not assigned'}</p>
                  {etaInfo.estimated_arrival_time && (
                    <p>Estimated arrival: {moment(etaInfo.estimated_arrival_time).format('HH:mm')}</p>
                  )}
                  {etaInfo.delay_time > 0 && (
                    <p>Delayed by: {Math.ceil(etaInfo.delay_time / 60)} min</p>
                  )}
                  {etaInfo.tracking_url && (
                    <p><a href={etaInfo.tracking_url} target="_blank" rel="noopener noreferrer">View tracking</a></p>
                  )}
                </div>
              }
            >
              <div className="flex items-center text-xs">
                <Badge status="processing" />
                <Space size={2}>
                  <ClockCircleOutlined className="text-blue-500" />
                  <span>
                    {etaInfo.onfleet_eta ? `ETA: ${etaInfo.onfleet_eta} min` : 'ETA: Calculating...'}
                  </span>
                </Space>

              </div>
            </Tooltip>
          ) : null}


        </div>
      </div>
    );
  }

  return getOrderStatusTag(record);
};

export default OrderStatusCell;
